import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { X, Plus, Clock, Trash2, Download } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const hm = (m) => `${Math.floor((m || 0) / 60)}h ${(m || 0) % 60}m`;
const fmt = (d) => { try { return d ? new Date(d).toLocaleDateString("en-GB") : "—"; } catch { return d; } };

export default function TimeManager() {
  const { can } = useAuth();
  const [sp, setSp] = useSearchParams();
  const matterFilter = sp.get("matter");
  const matterRef = sp.get("ref");
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [matters, setMatters] = useState([]);
  const [scope, setScope] = useState("mine");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ matter_id: matterFilter || "", minutes: 30, activity: "", date: new Date().toISOString().slice(0, 10), billable: true, notes: "" });

  const load = useCallback(() => {
    api.get("/admin/time", { params: matterFilter ? { matter_id: matterFilter } : { scope } }).then((r) => setItems(r.data)).catch(() => {});
    api.get("/admin/time/summary", { params: { scope } }).then((r) => setSummary(r.data)).catch(() => {});
  }, [scope, matterFilter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/matters").then((r) => setMatters(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/time", form); toast.success("Time logged"); setCreating(false); setForm({ matter_id: matterFilter || "", minutes: 30, activity: "", date: new Date().toISOString().slice(0, 10), billable: true, notes: "" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (t) => { await api.delete(`/admin/time/${t.id}`); toast.success("Deleted"); load(); };

  const card = "rounded-xl border border-slate-200 bg-white p-4";
  return (
    <div data-testid="time-manager">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div><h1 className="font-serif text-3xl font-bold text-navy">Time tracking</h1><p className="mt-1 text-sm text-slate-500">Record time against existing matters — no separate case system.</p></div>
        {can("time", "create") && <button onClick={() => setCreating(true)} data-testid="new-time-btn" className="btn-gold text-sm"><Plus size={15} /> Log time</button>}
      </div>

      {matterFilter && (<div data-testid="matter-filter-banner" className="mb-4 flex items-center justify-between rounded-lg border border-gold/40 bg-gold/10 px-4 py-2.5 text-sm text-navy"><span>Showing time entries for matter <b>{matterRef || matterFilter}</b></span><button onClick={() => setSp({})} data-testid="clear-matter-filter" className="text-xs font-medium underline hover:no-underline">Clear filter</button></div>)}

      <div className="mb-4 flex rounded-lg border border-slate-200 p-0.5 w-fit">
        <button onClick={() => setScope("mine")} className={`rounded-md px-3 py-1.5 text-sm ${scope === "mine" ? "bg-navy text-white" : "text-slate-500"}`}>Mine</button>
        <button onClick={() => setScope("all")} className={`rounded-md px-3 py-1.5 text-sm ${scope === "all" ? "bg-navy text-white" : "text-slate-500"}`}>All staff</button>
      </div>

      {summary && (
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className={card} data-testid="time-total"><div className="text-xs text-slate-400">Total logged</div><div className="mt-1 text-2xl font-bold text-navy">{hm(summary.total_minutes)}</div></div>
          <div className={card}><div className="text-xs text-slate-400">Billable</div><div className="mt-1 text-2xl font-bold text-emerald-600">{hm(summary.billable_minutes)}</div></div>
          <div className={card}><div className="text-xs text-slate-400">Non-billable</div><div className="mt-1 text-2xl font-bold text-slate-500">{hm(summary.non_billable_minutes)}</div></div>
          <div className={card}><div className="text-xs text-slate-400">Entries</div><div className="mt-1 text-2xl font-bold text-navy">{summary.entry_count}</div></div>
        </div>
      )}

      <div className="space-y-2">
        {items.map((t) => (
          <div key={t.id} data-testid={`time-row-${t.id}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-4">
            <Clock size={16} className="text-gold" />
            <div className="min-w-[160px] flex-1"><div className="font-medium text-navy">{t.activity}</div><div className="text-xs text-slate-400">{t.matter_ref} · {t.staff} · {fmt(t.date)}</div></div>
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">{hm(t.minutes)}</span>
            <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${t.billable ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>{t.billable ? "Billable" : "Non-billable"}</span>
            {can("time", "edit") && <button onClick={() => del(t)} className="rounded-md border border-slate-200 p-1.5 text-slate-500 hover:text-red-500"><Trash2 size={15} /></button>}
          </div>
        ))}
        {items.length === 0 && <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-400">No time entries yet.</div>}
      </div>

      {creating && (
        <div className="fixed inset-0 z-[55] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setCreating(false)} />
          <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between"><h2 className="font-serif text-xl font-semibold text-navy">Log time</h2><button onClick={() => setCreating(false)} className="text-slate-400"><X size={18} /></button></div>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Matter</Label><select data-testid="time-matter" value={form.matter_id} onChange={(e) => setForm({ ...form, matter_id: e.target.value })} required className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"><option value="">Select a matter…</option>{matters.map((m) => <option key={m.id} value={m.id}>{m.ref} — {m.client_name}</option>)}</select></div>
              <div><Label>Activity</Label><Input data-testid="time-activity" value={form.activity} onChange={(e) => setForm({ ...form, activity: e.target.value })} required className="mt-1" placeholder="e.g. Drafting application" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Minutes</Label><Input type="number" min="1" data-testid="time-minutes" value={form.minutes} onChange={(e) => setForm({ ...form, minutes: Number(e.target.value) })} className="mt-1" /></div>
                <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1" /></div>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={form.billable} onChange={(e) => setForm({ ...form, billable: e.target.checked })} data-testid="time-billable" className="h-4 w-4" /> Billable</label>
              <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" /></div>
              <button type="submit" data-testid="time-submit" className="btn-gold w-full">Save entry</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
